import React from 'react';
import { Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { IconBackground, ThreeDotMenu } from 'common/components';
import { EColor } from 'common/const';
import { EN_ALPHABET, RU_ALPHABET } from 'common/config';
import { Delivery, Tick } from 'common/svg';
import { IAccountShort } from 'common/models';
import { IAccount, IAccountBrand } from 'entities/Account/Account.models';
import { EAccountStatus, EAccountStatusTitle } from 'entities/Account/Account.enum';
import { IUser } from 'entities/User/User.models';
import { getUserName } from 'entities/User/User.helper';

export const getIsSelfRegistered = (account?: IAccountShort): boolean => {
  return account?.isSelfRegistered ?? false;
};

const getAccountStatusColor = (status: string) => {
  if (status === 'active') {
    return EColor.Blue;
  }

  return EColor.Gray;
};

export const getBrandsMap = (brands: IAccountBrand[]) => {
  const map = new Map<string, IAccountBrand[]>();
  const { ru, en, other } = splitBrandsByLanguage(brands);

  RU_ALPHABET.forEach((letter) => {
    const filtered = ru.filter((item) => item.brand.startsWith(letter));

    map.set(letter, filtered);
  });

  EN_ALPHABET.forEach((letter) => {
    const filtered = en.filter((item) => item.brand.startsWith(letter));

    map.set(letter, filtered);
  });

  map.set('other', other);

  return map;
};

export const filterAccountUsers = (search: string, userList?: IUser[]) => {
  if (!userList) {
    return [];
  }

  return userList.filter((user) => {
    return (
      getUserName.fullName(user.lastName, user.firstName, user.patronymic).toLowerCase().includes(search.toLowerCase()) ||
      (user.email || '')?.toLowerCase().includes(search.toLowerCase())
    );
  });
};

interface IBrandGroups {
  ru: IAccountBrand[];
  en: IAccountBrand[];
  other: IAccountBrand[];
}

const splitBrandsByLanguage = (brands: IAccountBrand[]): IBrandGroups => {
  return brands.reduce<IBrandGroups>(
    (acc, item) => {
      const trimmed = item.brand.trim();

      if (!trimmed) {
        acc.other.push(item);
        return acc;
      }

      const firstChar = trimmed[0];

      if (/[А-Яа-яЁё]/.test(firstChar)) {
        acc.ru.push(item);
      } else if (/[A-Za-z]/.test(firstChar)) {
        acc.en.push(item);
      } else {
        acc.other.push(item);
      }

      return acc;
    },
    { ru: [], en: [], other: [] },
  );
};

export const searchAccountsByName = (accounts: IAccount[], search: string) => {
  return accounts.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
};

export const renderAccountListRecords = (
  handleLoginToAccountClick: (id: number, isSeller: boolean) => void,
  handleEditClick: (account: IAccount) => void,
  handleBlockClick: (accountId: number) => void,
  handleUnblockClick: (accountId: number) => void,
  handleDeleteClick: (accountId: number) => void,
  handleMakeSellerClick: (id: number) => void,
) => {
  return [
    {
      key: 'status',
      onCell: () => {
        return {
          className: 'account-list__cell-status',
        };
      },
      render: (_, record: IAccount) => (
        <div className={`status ${getAccountStatusColor(record.status)}`}>{EAccountStatusTitle[record.status]}</div>
      ),
    },
    {
      key: 'id',
      onCell: () => {
        return {
          className: 'account-list__cell-id',
        };
      },
      render: (_, record) => <span className="account-list__item-id">{record.id}</span>,
      ellipsis: true,
    },
    {
      key: 'name',
      render: (_, record) => <span className="account-list__item-name">{record.name}</span>,
      ellipsis: true,
    },
    {
      key: 'tariffPlan',
      onCell: () => {
        return {
          className: 'account-list__cell-tariffPlan',
        };
      },
      render: (_, record) => (
        <div className="account-list__item-tariffPlan">
          <span>{record.tariffPlan}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      onCell: () => {
        return {
          className: 'account-list__cell-actions',
        };
      },
      render: (_, record) => (
        <ThreeDotMenu
          items={[
            {
              key: 'login',
              label: (
                <Button
                  onClick={() => handleLoginToAccountClick(record.id, record.isSeller)}
                  disabled={record.status === EAccountStatus.Blocked}
                >
                  Войти в аккаунт
                </Button>
              ),
              className: 'login',
            },
            {
              key: 'createSeller',
              label: (
                <Button onClick={() => handleMakeSellerClick(record.id)} disabled={record.isSeller}>
                  Сделать продавцом
                </Button>
              ),
              className: 'createSeller',
            },
            {
              key: 'edit',
              label: (
                <Button onClick={() => handleEditClick(record)} disabled={record.status === EAccountStatus.Blocked}>
                  Редактировать
                </Button>
              ),
              className: 'edit',
            },
            {
              key: 'block',
              label: (
                <Button
                  onClick={() => {
                    if (record.status === EAccountStatus.Blocked) {
                      handleUnblockClick(record.id);
                    } else {
                      handleBlockClick(record.id);
                    }
                  }}
                >
                  {record.status === EAccountStatus.Blocked ? 'Разблокировать' : 'Заблокировать'}
                </Button>
              ),
              className: 'block',
            },
            {
              key: 'delete',
              label: <Button onClick={() => handleDeleteClick(record.id)}>Удалить</Button>,
              className: 'delete',
            },
          ]}
        />
      ),
    },
  ] as ColumnsType<IAccount>;
};

export const renderAccountOption = (name: string, id: number, selected = false, onClick?: (id: number) => void) => {
  return (
    <div key={id} className="account-option" onClick={() => onClick?.(id)}>
      <IconBackground content={<Delivery className="icon-delivery-dark-grey" />} color={EColor.LightGrey} />
      <div className="flex-1 flex align-center justify-between">
        <span className="ff-regular fs-s ellipsis">{name}</span>
        {selected && <Tick className="icon-bright-green" style={{ minWidth: 20 }} />}
      </div>
    </div>
  );
};
